import i18n from '../../i18n';
import {
  getBrandPhonePlaceholder,
  isBrandPhoneValid,
  normalizeBrandPhone,
  SAUDI_PHONE_PREFIX,
} from './brandCountry';
import { getBrandLocale, SAUDI_CURRENCY_CODE } from './brandCurrency';

const pp = (key, opts) => i18n.t(`modules.shared.profile.${key}`, opts);

/** Default Saudi profile & localization preferences. */
export const DEFAULT_PROFILE_PREFS = {
  language: 'ar',
  showHijri: false,
  dateLocale: 'ar-SA',
  numberLocale: 'ar-SA',
  currency: SAUDI_CURRENCY_CODE,
};

/** Notification preference keys persisted with the user profile. */
export const DEFAULT_NOTIFICATION_PREFS = {
  emailNotifications: true,
  smsNotifications: false,
  wpsPaymentAlerts: true,
  gosiDeadlineReminders: true,
  systemAlerts: true,
};

const PASSWORD_RULES = {
  minLength: 8,
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  number: /[0-9]/,
  special: /[^A-Za-z0-9]/,
};

/**
 * Validate password complexity (Saudi ERP security policy).
 * @returns {string|null} rule key or null when valid
 */
export function validateProfilePassword(password) {
  const value = String(password || '');
  if (value.length < PASSWORD_RULES.minLength) return 'min_length';
  if (!PASSWORD_RULES.uppercase.test(value)) return 'uppercase';
  if (!PASSWORD_RULES.lowercase.test(value)) return 'lowercase';
  if (!PASSWORD_RULES.number.test(value)) return 'number';
  if (!PASSWORD_RULES.special.test(value)) return 'special';
  return null;
}

/** Returns true when phone is empty or valid for the active brand (+966 Saudi). */
export function validateProfilePhone(value) {
  return isBrandPhoneValid(value);
}

export function normalizeProfilePhone(value) {
  return normalizeBrandPhone(value);
}

export function getProfilePhonePlaceholder() {
  return getBrandPhonePlaceholder();
}

/**
 * Format dates for profile views (ar-SA + optional Hijri).
 */
export function formatProfileDate(value, opts = {}) {
  const { showHijri = false, language = 'ar' } = opts;
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';

  const locale = getBrandLocale(language);
  const gregorian = d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (!showHijri) return gregorian;

  try {
    const hijri = new Intl.DateTimeFormat('ar-SA-u-ca-islamic-umalqura', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(d);
    return `${gregorian} (${hijri})`;
  } catch {
    return gregorian;
  }
}

function readJsonField(storageKey, field) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return String(parsed?.[field] || '').trim();
  } catch {
    return '';
  }
}

function readSetupRegistrationPhone() {
  try {
    const raw = localStorage.getItem('setup_data');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return String(parsed?.company?.phone || '').trim();
  } catch {
    return '';
  }
}

/** Resolve phone from session, settings, or setup registration. */
export function resolveProfilePhone({ storedUser, settingsBlob, permissionsUser } = {}) {
  const candidates = [
    storedUser?.phone,
    settingsBlob?.adminUser?.phone,
    settingsBlob?.company?.phone,
    readSetupRegistrationPhone(),
    readJsonField('qawaem_admin_user', 'phone'),
    readJsonField('qawaem_user', 'phone'),
    permissionsUser?.phone,
  ];
  for (const value of candidates) {
    const trimmed = value != null ? String(value).trim() : '';
    if (trimmed) return trimmed;
  }
  return '';
}

/** Load persisted profile preferences from localStorage. */
export function loadProfilePreferences() {
  try {
    const raw = localStorage.getItem('qawaem_profile_prefs');
    if (!raw) return { ...DEFAULT_PROFILE_PREFS, notifications: { ...DEFAULT_NOTIFICATION_PREFS } };
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_PROFILE_PREFS,
      ...parsed,
      notifications: { ...DEFAULT_NOTIFICATION_PREFS, ...(parsed.notifications || {}) },
    };
  } catch {
    return { ...DEFAULT_PROFILE_PREFS, notifications: { ...DEFAULT_NOTIFICATION_PREFS } };
  }
}

/** Persist profile preferences to localStorage. */
export function saveProfilePreferences(prefs) {
  try {
    localStorage.setItem('qawaem_profile_prefs', JSON.stringify(prefs));
  } catch (err) {
    console.warn('Failed to save profile preferences:', err);
  }
}

/** Sync profile fields across qawaem_user / qawaem_admin_user / secure storage. */
export function syncLocalUserProfile(updates, { secureGetItem, secureSetItem } = {}) {
  const trimmedUsername = updates.username?.trim() || updates.name?.trim() || '';
  try {
    const userJson = localStorage.getItem('qawaem_user');
    if (userJson) {
      const userData = JSON.parse(userJson);
      if (trimmedUsername) userData.username = trimmedUsername;
      if (updates.nameAr !== undefined) userData.nameAr = updates.nameAr;
      if (updates.email !== undefined) userData.email = updates.email;
      if (updates.jobTitle !== undefined) userData.jobTitle = updates.jobTitle;
      if (updates.phone !== undefined) userData.phone = updates.phone;
      if (updates.department !== undefined) userData.department = updates.department;
      if (updates.profilePhoto !== undefined) userData.profilePhoto = updates.profilePhoto;
      userData.updatedAt = new Date().toISOString();
      localStorage.setItem('qawaem_user', JSON.stringify(userData));
    }

    const adminJson = localStorage.getItem('qawaem_admin_user');
    if (adminJson) {
      const adminData = JSON.parse(adminJson);
      if (trimmedUsername) adminData.username = trimmedUsername;
      if (updates.email !== undefined) adminData.email = updates.email;
      if (updates.jobTitle !== undefined) adminData.jobTitle = updates.jobTitle;
      if (updates.phone !== undefined) adminData.phone = updates.phone;
      if (updates.department !== undefined) adminData.department = updates.department;
      localStorage.setItem('qawaem_admin_user', JSON.stringify(adminData));
    }

    if (secureGetItem && secureSetItem) {
      const secureUser = secureGetItem('qawaem_user');
      if (secureUser) {
        if (trimmedUsername) secureUser.username = trimmedUsername;
        if (updates.nameAr !== undefined) secureUser.nameAr = updates.nameAr;
        if (updates.email !== undefined) secureUser.email = updates.email;
        if (updates.jobTitle !== undefined) secureUser.jobTitle = updates.jobTitle;
        if (updates.phone !== undefined) secureUser.phone = updates.phone;
        if (updates.department !== undefined) secureUser.department = updates.department;
        if (updates.profilePhoto !== undefined) secureUser.profilePhoto = updates.profilePhoto;
        secureUser.updatedAt = new Date().toISOString();
        secureSetItem('qawaem_user', secureUser);
      }
    }
  } catch (storageErr) {
    console.warn('Error syncing local user profile:', storageErr);
  }
}

/** Backend mirror of Saudi phone validation (+966). */
export function isSaudiPhoneValidServer(phone) {
  const raw = String(phone || '').trim();
  if (!raw) return true;
  const digits = raw.replace(/\D/g, '');
  if (!digits.startsWith('966')) return false;
  return digits.length >= 12 && digits.length <= 13;
}

export function normalizeSaudiPhoneServer(phone) {
  const raw = String(phone || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('966')) return `+${digits}`;
  if (digits.startsWith('0') && digits.length >= 10) return `${SAUDI_PHONE_PREFIX}${digits.slice(1)}`;
  if (/^5\d{8}$/.test(digits)) return `${SAUDI_PHONE_PREFIX}${digits}`;
  return raw.startsWith('+') ? raw : raw;
}

/** Backend password complexity check. */
export function validatePasswordComplexityServer(password) {
  const rule = validateProfilePassword(password);
  if (!rule) return null;
  return {
    code: 'WEAK_PASSWORD',
    error: pp(`password_rule_${rule}`) || pp('password_complexity_failed'),
  };
}
